import * as React from "react";
import { RdPopover } from "../../redesign/table/RdPopover";
import { RD_MONTH_GRID_BREITE, RdMonthGrid } from "../../calendar/RdDatePicker";
import { toDateInputValue } from "../../../utils/dateUtils";
import { twvDateView } from "./twvFrist";
import "./vorgangDetail.css";
import { twvPatch } from "../shared/fristZurTwv";

/**
 * Zeile „TWV | Frist" (Detail-Remake, SP7-Redesign, T2):
 *  - kein natives `<input type="date">` mehr, sondern ein KALENDERBLATT
 *    (abreißkalender-Optik: Monatskappe + Tageszahl) neben Wochentag + relativem
 *    Wort („Di. · übermorgen", B12). Blattkappe rot bei überfällig, sonst
 *    Aubergine. Das Jahr erscheint nur, wenn es nicht das aktuelle Jahr ist;
 *  - Klick öffnet ein Popover mit dem kvp-rd-Design-Datepicker (RdMonthGrid);
 *    Auswahl speichert sofort (Auto-Save über onChange der Seite). Editierbare
 *    Felder zeigen beim Hovern einen Stift als Affordance;
 *  - optional (nur auf den Detailseiten): Aktion „Zeitblock im Kalender"
 *    (30 Min., persönlicher Kalender, Entscheidung 48) via `onSetBlock`.
 */
type Props = {
    twv?: string | null;
    frist?: string | null;
    editable: boolean;
    onChange: (patch: { twv?: string | null; frist?: string | null }) => void;
    /** Optional (nur Detailseiten, SP7): „Zeitblock im Kalender" setzen. */
    onSetBlock?: () => void;
};

const PencilIcon = (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
        <path d="M13.5 4.5l2 2M4 16l.7-3 8-8 2.3 2.3-8 8L4 16z" />
    </svg>
);

/** Kalenderblatt (Monatskappe + Tag) + Wochentag/Jahr + relatives Wort; „—" wenn leer. */
const CalendarLeaf: React.FC<{ value?: string | null }> = ({ value }) => {
    const view = twvDateView(value);
    if (!view) return <span className="vgd-daterel vgd-daterel--empty">—</span>;
    const overdue = view.tone === "overdue";
    const mon = view.monthShort.replace(/\.$/, "").toUpperCase();
    const full = view.date + (view.relative ? ` · ${view.relative}` : "");
    return (
        <span className="vgd-caldate" title={full}>
            <span className={`vgd-cal-leaf${overdue ? " vgd-cal-leaf--overdue" : ""}`} aria-hidden="true">
                <span className="vgd-cal-leaf-mon">{mon}</span>
                <span className="vgd-cal-leaf-day">{view.day}</span>
            </span>
            <span className="vgd-cal-meta">
                <span className="vgd-cal-wd">
                    {view.weekday}
                    {view.year && <span className="vgd-cal-year"> {view.year}</span>}
                </span>
                {view.relative && (
                    <span className={`vgd-daterel vgd-daterel--${view.tone}`}>{view.relative}</span>
                )}
            </span>
        </span>
    );
};

const parseIso = (v?: string | null): Date => {
    const s = v ? String(v).slice(0, 10) : "";
    return s ? new Date(`${s}T00:00:00`) : new Date();
};

const TwvDateField: React.FC<{
    label: string;
    title?: string;
    value?: string | null;
    editable: boolean;
    onSave: (next: string | null) => void;
}> = ({ label, title, value, editable, onSave }) => {
    if (!editable) {
        return (
            <div className="vgd-field">
                <span className="vgd-label" title={title}>
                    {label}
                </span>
                <CalendarLeaf value={value} />
            </div>
        );
    }
    return (
        <div className="vgd-field">
            <span className="vgd-label" title={title}>
                {label}
            </span>
            <RdPopover
                align="start"
                width={RD_MONTH_GRID_BREITE}
                trigger={({ open, toggle, ref }) => (
                    <button
                        ref={ref}
                        type="button"
                        className={`vgd-datetrigger${value ? "" : " vgd-datetrigger--empty"}`}
                        onClick={toggle}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        title={value ? "Datum ändern" : undefined}
                    >
                        {value ? (
                            <>
                                <CalendarLeaf value={value} />
                                <span className="vgd-cal-pencil" aria-hidden="true">{PencilIcon}</span>
                            </>
                        ) : (
                            <span>+ Datum setzen</span>
                        )}
                    </button>
                )}
            >
                {(close) => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <RdMonthGrid
                            value={parseIso(value)}
                            onPick={(d) => {
                                onSave(toDateInputValue(d));
                                close();
                            }}
                        />
                        {value && (
                            <button
                                type="button"
                                className="vgd-linkval"
                                style={{ alignSelf: "flex-start", fontSize: "0.82rem", fontWeight: 600 }}
                                onClick={() => {
                                    onSave(null);
                                    close();
                                }}
                            >
                                Datum entfernen
                            </button>
                        )}
                    </div>
                )}
            </RdPopover>
        </div>
    );
};

export const TwvFristRow: React.FC<Props> = ({ twv, frist, editable, onChange, onSetBlock }) => (
    <div className="vgd" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="vgd-rowgrid" style={{ gridTemplateColumns: "repeat(2, minmax(160px, 1fr))" }}>
            <TwvDateField
                label="TWV"
                title="Termin-Wiedervorlage (Reminder)"
                value={twv}
                editable={editable}
                onSave={(next) => onChange(twvPatch(next, frist))}
            />
            <TwvDateField
                label="Frist"
                value={frist}
                editable={editable}
                onSave={(next) => onChange({ frist: next })}
            />
        </div>
        {editable && onSetBlock && (
            <button type="button" className="vgd-linkval vgd-blocklink" onClick={onSetBlock}>
                ＋ Zeitblock im Kalender (30 Min.)
            </button>
        )}
    </div>
);
